// Sparkle-grid background — tune values here.

export const HOME_BG_PATTERN_ENABLED = true;
export const HOME_BG_PATTERN_FADE_END = '100vh';
export const HOME_BG_PATTERN_FADE_ANGLE = '0deg';
/** Radial vignette: pattern fades out toward this point (viewport-relative). */
export const HOME_BG_PATTERN_VIGNETTE_X = '50%';
export const HOME_BG_PATTERN_VIGNETTE_Y = '120vh';
export const HOME_BG_PATTERN_VIGNETTE_INNER = '1vmin';
export const HOME_BG_PATTERN_VIGNETTE_OUTER = '50vw';

export const HOME_CROSS_PATTERN = {
    tileSize: 36, // smaller tile → denser grid
    scale: 1, // grid spacing (tile px = tileSize × scale) — not hover
    crossInset: 13.3,
    bgFill: 'transparent',
    stroke: 'rgba(94, 223, 159, 0.49)', // theme primary (dark); light theme: #2ebd84
    strokeWidth: 0.5,
    strokeLinecap: 'square',
    opacity: 1,
};

// Cursor hover — all tunables here (separate from HOME_CROSS_PATTERN).
export const HOME_CURSOR_HOVER = {
    radius: 60,
    falloffStart: 0,
    falloffEnd: null,

    opacityBoost: 0.5,
    maxOpacity: 1,

    scaleAtCenter: null,
    crossInsetAtCenter: 10,

    strokeWidthBoost: null,
    stroke: null,

    matrix: [1, 0, 0, 1],
};

// Scroll — each property uses the same axis formula:
//   value = offset + scrollY×perPx + col×perCol + row×perRow
// Omit a block (or set to null) to leave that property unchanged by scroll.
export const HOME_SCROLL = {
    enabled: false, // rotate-on-scroll removed — re-enable to drive the axes below

    rotation: {
        unit: 'deg', // 'deg' | 'rad'
        offset: 0,
        perPx: 0.1,
        perCol: 0,
        perRow: 0,
    },

    opacity: {
        mode: 'add', // 'add' | 'set'
        max: 1,
        offset: 0,
        perPx: 0.00,
        perCol: 0,
        perRow: 0,
    },

    drawScale: {
        mode: 'add', // 'add' (offset added to 1) | 'mul' (multiply drawScale)
        offset: 0,
        perPx: 0.00,
        perCol: 0,
        perRow: 0,
    },

    strokeWidth: {
        mode: 'add', // 'add' | 'mul' (1+v) | 'set'
        offset: 0,
        perPx: 0,
        perCol: 0,
        perRow: 0,
    },

    crossInset: {
        mode: 'add', // 'add' | 'set'
        offset: 0,
        perPx: 0,
        perCol: 0,
        perRow: 0,
    },

    stroke: null, // fixed color override, or null
};

// Static vertical gradient — shapes smaller/transparent on top, bigger/opaque at bottom.
//   topInset / bottomInset: cross inset per row (smaller = BIGGER diamond; radius = tileSize/2 − inset)
//   curve:        size easing exponent over the vertical span (1 = linear, >1 = grows late)
//   topOpacity / bottomOpacity: alpha at the top vs bottom row (transparent → opaque)
//   opacityCurve: alpha easing exponent (1 = linear fade)
export const HOME_VERTICAL_GRADIENT = {
    enabled: true,
    topInset: 16.5,
    bottomInset: 16.5,
    curve: 2.5,
    topOpacity: 0.5,
    bottomOpacity: 5,
    opacityCurve: 1,
};

export const HOME_GRID_EFFECTS_CONFIG = {
    cursor: HOME_CURSOR_HOVER,
    scroll: HOME_SCROLL,
    verticalGradient: HOME_VERTICAL_GRADIENT,
};

export const tilePx = (config) => config.tileSize * config.scale;

/** Half-size of a cross in canvas px (for edge padding). */
export const crossHalfReach = (config, cellScale = 1) => (
    Math.ceil((config.tileSize / 2) * cellScale) + 1
);

/** Evenly space crosses from x=0 to x=width; tileSize is the target step. */
export const fitGridX = (width, config) => {
    const targetStep = tilePx(config);
    const intervals = Math.max(1, Math.round(width / targetStep));
    return {
        cols: intervals + 1,
        stepX: width / intervals,
    };
};

export const buildCrossGridPath = ({ tileSize, crossInset }) => {
    const center = tileSize / 2;
    const arm = tileSize - crossInset * 2;
    return `M${crossInset} ${center}h${arm}M${center} ${crossInset}v${arm}`;
};

export const drawCross = (ctx, size, inset, props) => {
    const center = size / 2;
    const arm = size - inset * 2;
    const { stroke, strokeWidth, strokeLinecap, opacity, bgFill } = props;

    ctx.save();
    ctx.globalAlpha = opacity ?? 1;
    if (bgFill && bgFill !== 'transparent') {
        ctx.fillStyle = bgFill;
        ctx.fillRect(0, 0, size, size);
    }
    ctx.strokeStyle = stroke;
    ctx.lineWidth = strokeWidth;
    ctx.lineCap = strokeLinecap;
    ctx.beginPath();
    ctx.moveTo(inset, center);
    ctx.lineTo(inset + arm, center);
    ctx.moveTo(center, inset);
    ctx.lineTo(center, inset + arm);
    ctx.stroke();
    ctx.restore();
};

// 4-pointed diamond with concave (bezier) sides — the "AI spark" shape.
// Uses fill so strokeWidth is irrelevant; stroke color is used as fill.
export const drawSparkle = (ctx, size, inset, props) => {
    const cx = size / 2;
    const cy = size / 2;
    const r = cx - inset;
    if (r <= 0) return;
    const { stroke, opacity, bgFill } = props;

    // 0 = diamond, 1 = maximum pinch; ~0.85 gives the sharp AI-spark look
    const concavity = 0.85;
    const cp = r * (1 - concavity);

    ctx.save();
    ctx.globalAlpha = opacity ?? 1;
    if (bgFill && bgFill !== 'transparent') {
        ctx.fillStyle = bgFill;
        ctx.fillRect(0, 0, size, size);
    }
    ctx.fillStyle = stroke;
    ctx.beginPath();
    ctx.moveTo(cx, cy - r);
    ctx.quadraticCurveTo(cx + cp, cy - cp, cx + r, cy);
    ctx.quadraticCurveTo(cx + cp, cy + cp, cx, cy + r);
    ctx.quadraticCurveTo(cx - cp, cy + cp, cx - r, cy);
    ctx.quadraticCurveTo(cx - cp, cy - cp, cx, cy - r);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
};
